'use client';

import type { ReactNode } from 'react';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../primitives/dropdown-menu';

// ============================================================================
// Types
// ============================================================================

type Props = {
  /**
   * The trigger element that will be displayed and duplicated in the dropdown header
   */
  trigger: ReactNode;
  /**
   * Optional custom content for the header (if not provided, uses trigger content)
   * Useful for showing trigger without interactive elements like chevrons
   */
  headerContent?: ReactNode;
  /**
   * The dropdown content (menu items, etc.)
   */
  children: ReactNode;
  /**
   * Additional class name for the trigger wrapper
   */
  triggerClassName?: string;
  /**
   * Additional class name for the dropdown content
   */
  contentClassName?: string;
  /**
   * Additional class name for the duplicated trigger in the header
   */
  headerClassName?: string;
  /**
   * Alignment of the dropdown content relative to the trigger
   * @default 'start'
   */
  align?: 'start' | 'center' | 'end';
  /**
   * Width of the dropdown content
   * @default '240px'
   */
  width?: string;
  /**
   * Whether to show the border below the header
   * @default true
   */
  showHeaderBorder?: boolean;
  /**
   * Whether to show the duplicated trigger in the dropdown header
   * @default true
   */
  showDuplicateHeader?: boolean;
  /**
   * Whether the dropdown should be modal (trap focus and block interaction with outside elements)
   * Set to false when used inside another modal to prevent closing the parent modal
   * @default true
   */
  modal?: boolean;
  /**
   * Controlled open state. When provided, the dropdown becomes controlled.
   */
  open?: boolean;
  /**
   * Callback fired when the open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Offset along the side axis of the trigger
   */
  sideOffset?: number;
  /**
   * Offset along the align axis of the trigger
   */
  alignOffset?: number;
  /**
   * Padding (in px) Radix keeps between the content edge and the viewport
   * boundary when collision-shifting. Use this to leave a visible gutter on
   * both sides when the content is nearly as wide as the viewport.
   */
  collisionPadding?: number;
  /**
   * Container element for the Radix portal. Use when rendering inside a
   * picture-in-picture / Document PiP window so the content mounts in that
   * window's document.
   */
  container?: HTMLElement | null;
};

// ============================================================================
// Dropdown Component
// ============================================================================

/**
 * A dropdown menu that overlaps its trigger element, with the trigger content
 * duplicated in the dropdown header for a seamless expansion effect.
 *
 * @example
 * ```tsx
 * <Dropdown
 *   trigger={
 *     <div className="flex items-center gap-2">
 *       <Avatar />
 *       <span>John Doe</span>
 *     </div>
 *   }
 * >
 *   <DropdownMenuItem>Settings</DropdownMenuItem>
 *   <DropdownMenuItem>Log out</DropdownMenuItem>
 * </Dropdown>
 * ```
 */
export const Dropdown = ({
  trigger,
  headerContent,
  children,
  triggerClassName,
  contentClassName,
  headerClassName,
  align = 'start',
  width = '240px',
  showHeaderBorder = true,
  showDuplicateHeader = true,
  modal = true,
  open,
  onOpenChange,
  sideOffset,
  alignOffset,
  collisionPadding,
  container,
}: Props) => {
  return (
    <DropdownMenu modal={modal} open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-1.5 w-full p-1 pr-2 rounded-xl hover:bg-primary-alpha-5 transition-colors outline-none',
            triggerClassName,
          )}
        >
          {trigger}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          'bg-white border-primary/5 rounded-xl shadow-md p-2',
          showDuplicateHeader && '-mt-[51px]',
          contentClassName,
        )}
        style={{ width }}
        align={align}
        side="bottom"
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        container={container}
      >
        {/* Duplicated trigger header */}
        {showDuplicateHeader && (
          <div
            className={cn(
              'px-1 pb-2 mb-2',
              showHeaderBorder && 'border-b border-primary-10',
              headerClassName,
            )}
          >
            <div className="flex items-center gap-1.5 p-1 rounded-xl">
              {headerContent ?? trigger}
            </div>
          </div>
        )}

        {/* Menu content */}
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
